import { logException } from "@um/ui";
import {
  doc,
  FieldValue,
  getDoc,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "@um/ui";

export interface DeferredDeepLink {
  url: string;
  createdAt: FieldValue;
  status: "pending" | "consumed" | "expired";
  source: string;
}

// Time constants
export const MS_PER_HOUR = 60 * 60 * 1000; // Milliseconds in an hour
export const MS_PER_DAY = 24 * MS_PER_HOUR; // Milliseconds in a day

// Deferred deep link storage
const DEFERRED_LINK_EXPIRY_DAYS = 1;

/**
 * Store deferred deep link in Firebase user document
 */
export async function storeDeferredDeepLinkInFirebase(
  userId: string,
  link: string,
  source: string = "web-auth"
): Promise<void> {
  try {
    const deferredLink: DeferredDeepLink = {
      url: link,
      createdAt: serverTimestamp(),
      status: "pending",
      source,
    };

    const userRef = doc(db, "User", userId);
    await updateDoc(userRef, {
      deferredDeepLink: deferredLink,
    });

    console.log("[DeepLink] Stored deferred link in Firebase:", deferredLink);
  } catch (error) {
    logException(error);
    console.error("[DeepLink] Error storing deferred link in Firebase:", error);
    throw error;
  }
}

/**
 * Clear deferred deep link from Firebase user document
 */
export async function clearDeferredDeepLinkFromFirebase(
  userId: string
): Promise<void> {
  try {
    const userRef = doc(db, "User", userId);
    await updateDoc(userRef, {
      deferredDeepLink: null,
    });

    console.log("[DeepLink] Cleared deferred link from Firebase");
  } catch (error) {
    logException(error);
    console.error(
      "[DeepLink] Error clearing deferred link from Firebase:",
      error
    );
    throw error;
  }
}

export async function getDeferredDeepLinkFromFirebase(
  userId: string
): Promise<DeferredDeepLink | null> {
  const userRef = doc(db, "User", userId);
  const userDoc = await getDoc(userRef);
  const deferredDeepLink = userDoc.data()?.deferredDeepLink;

  if (deferredDeepLink) {
    if (isExpiredDeferredLink(deferredDeepLink)) {
      await clearDeferredDeepLinkFromFirebase(userId);
      return null;
    }
    await clearDeferredDeepLinkFromFirebase(userId);
    return deferredDeepLink;
  }
  return null;
}

function isExpiredDeferredLink(link: DeferredDeepLink): boolean {
  const expiryTimestamp = Timestamp.fromDate(
    new Date(Date.now() - DEFERRED_LINK_EXPIRY_DAYS * MS_PER_DAY)
  );

  return link.createdAt < expiryTimestamp;
}

/**
 * Extract and normalize deep link from notification data
 * Extracts href from full URLs while preserving query parameters
 * Supports multiple field names where deep link might be stored
 */
export function extractAndNormalizeDeepLinkFromNotification(
  notification: any
): string | null {
  if (!notification) return null;

  let rawUrl: string | null = null;

  // Check for deep link in notification data
  if (notification.data) {
    rawUrl =
      notification.data.href ||
      notification.data.deepLink ||
      notification.data.deeplink ||
      notification.data.deep_link ||
      notification.data.url;
  }

  // Also check in the notification object itself
  if (!rawUrl) {
    rawUrl = notification.href || notification.deepLink || notification.url;
  }

  if (!rawUrl) return null;

  // If it's a full URL (http/https), extract the pathname and search params
  if (rawUrl.startsWith("https://") || rawUrl.startsWith("http://")) {
    const url = new URL(rawUrl);
    const href = url.pathname + url.search + url.hash;
    return href || "/";
  }
  if (rawUrl.startsWith("/")) {
    return rawUrl;
  }
  // Default: assume it's a path without leading slash
  return `/${rawUrl}`;
}

const PENDING_NAVIGATION_PATH_KEY = "pendingNavigationPath";

function pathnameFromUrlOrPath(value: string): string | null {
  try {
    if (value.startsWith("http://") || value.startsWith("https://")) {
      return new URL(value).pathname;
    }
    if (value.startsWith("/")) {
      return new URL(value, "https://app.uniqueminds.com").pathname;
    }
    return new URL(`/${value}`, "https://app.uniqueminds.com").pathname;
  } catch {
    return null;
  }
}

export function isRedeemNavigationPath(pathOrUrl: string): boolean {
  const pathname = pathnameFromUrlOrPath(pathOrUrl);
  return pathname?.startsWith("/redeem") ?? false;
}

/**
 * Records redeem intent before a hard navigation (e.g. push tap → window.location).
 * AppInitializer reads and clears this during init so onboarding resume does not
 * hijack /redeem when pathname updates lag behind.
 */
export function setPendingRedeemNavigationPath(path: string): void {
  if (!isRedeemNavigationPath(path)) return;
  try {
    sessionStorage.setItem(PENDING_NAVIGATION_PATH_KEY, path);
  } catch (error) {
    console.warn("[DeepLink] Failed to store pendingNavigationPath:", error);
  }
}

/** Returns a pending /redeem path if present, then clears it (read-once). */
export function consumePendingRedeemNavigationPath(): string | null {
  try {
    const pending = sessionStorage.getItem(PENDING_NAVIGATION_PATH_KEY);
    if (!pending || !isRedeemNavigationPath(pending)) {
      return null;
    }
    sessionStorage.removeItem(PENDING_NAVIGATION_PATH_KEY);
    return pending;
  } catch {
    return null;
  }
}

/**
 * True when the user is heading to /redeem via live route, sessionStorage intent,
 * or a pending OTA-restore URL — used to skip onboarding hijack during init.
 */
export function hasPendingRedeemNavigationIntent(): {
  skip: boolean;
  reason?: string;
} {
  if (typeof window !== "undefined") {
    if (window.location.pathname.startsWith("/redeem")) {
      return { skip: true, reason: "live_pathname" };
    }

    const pendingSessionPath = sessionStorage.getItem(
      PENDING_NAVIGATION_PATH_KEY
    );
    if (pendingSessionPath && isRedeemNavigationPath(pendingSessionPath)) {
      return { skip: true, reason: "sessionStorage_pendingNavigationPath" };
    }

    const pendingUpdateUrl = localStorage.getItem("pendingUpdateUrl");
    if (pendingUpdateUrl && isRedeemNavigationPath(pendingUpdateUrl)) {
      return { skip: true, reason: "localStorage_pendingUpdateUrl" };
    }
  }

  return { skip: false };
}
